import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AnalyticEvents } from '@app/enums';
import { KanbanColumn } from '@app/interfaces/kanban-column.interface';
import { AnalyticService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { JobFunnelNameOptions } from '@app/types/translatables/applicant-tracker/job-funnel-name.options';
import { KanbanApplicant } from '@applicant-tracker/interfaces/kanban-applicant.interface';
import { KanbanCard } from '../../interfaces/kanban-card.interface';

@Component({
    selector: 'app-kanban',
    templateUrl: './kanban.template.html',
    styleUrls: ['./kanban.style.scss'],
})
export class KanbanComponent {
    @Input() allowColumnCreation = false;
    @Input() allowColumnRename = false;
    @Input() allowColumnDeletion = false;
    @Input() columns: KanbanColumn[];
    @Input() isEmailAutomationForJobFunnel = false;
    @Input() isEmailAutomationForFunnelTemplateStage = false;
    @Output() columnOrderChanged: EventEmitter<KanbanColumn[]> = new EventEmitter<KanbanColumn[]>();
    @Output() columnAdded: EventEmitter<string> = new EventEmitter<string>();
    @Output() columnDeleted: EventEmitter<number> = new EventEmitter<number>();
    @Output() cardDropped: EventEmitter<KanbanApplicant> = new EventEmitter<KanbanApplicant>();
    @Output()
    columnNameUpdated: EventEmitter<{ id: number; name: string }> = new EventEmitter<{
        id: number;
        name: string;
    }>();
    @Output() applicantOrderChanged: EventEmitter<{
        applicantId: number;
        order: number;
        atsJobFunnelId: number;
        showMessages: boolean;
    }> = new EventEmitter<{
        applicantId: number;
        order: number;
        atsJobFunnelId: number;
        showMessages: boolean;
    }>();

    isRenameModalOpen = false;
    isDeleteModalOpen = false;
    temporaryColumnName: string;
    updatingColumn: KanbanColumn;
    cardHeight = 150;

    jobFunnelNameDisplay = JobFunnelNameOptions;

    constructor(
        public featureService: FeatureService,
        private analyticService: AnalyticService
    ) {}

    canDelete(): boolean {
        if (this.updatingColumn && this.updatingColumn.cards?.length > 0) {
            return false;
        }

        return true;
    }

    onCardOrderChanged(event: CdkDragDrop<KanbanColumn[]>, columnIndex: number): void {
        //Bug fixes inspired from https://stackoverflow.com/questions/65388751/previousindex-unexpectedly-jumps-with-angular-material-drag-drop-cdk-virtual-s
        const correctPreviousIndex = event.item.data.cardIndex;
        let newCorrectIndex = event.currentIndex;

        if (event.previousContainer === event.container) {
            //Fix indices inside virtual scroll (when reordering happens inside same container)
            const adjustment = event.previousIndex - correctPreviousIndex;
            newCorrectIndex = event.currentIndex - adjustment;

            moveItemInArray(this.columns[columnIndex].cards, correctPreviousIndex, newCorrectIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                correctPreviousIndex,
                newCorrectIndex
            );
        }

        event.item.data.card.index = newCorrectIndex;

        this.updateIndexesOfCards(this.columns);

        this.cardDropped.emit(event.item.data.card.data);
    }

    onColumnOrderChanged(event: CdkDragDrop<KanbanColumn[]>): void {
        moveItemInArray(this.columns, event.previousIndex, event.currentIndex);
        this.columns = this.updateIndexesOfColumns(this.columns);
        this.columnOrderChanged.emit(this.columns);
    }

    updateIndexesOfCards(columns: KanbanColumn[]): KanbanColumn[] {
        return columns.map((column: KanbanColumn) => {
            const updatedCards = column.cards.map((card: KanbanCard, index: number) => {
                card.data.atsJobFunnelId = column.data.id;
                card.index = index;
                card.data.funnelStatus = index;
                return card;
            });
            column.cards = updatedCards;
            return column;
        });
    }

    updateIndexesOfColumns(columns: KanbanColumn[]): KanbanColumn[] {
        return columns.map((column: KanbanColumn, index: number) => {
            column.index = index;
            return column;
        });
    }

    sortPredicate(index: number): boolean {
        if (!index) {
            return false;
        }
        return true;
    }

    closeRenameModal(): void {
        this.isRenameModalOpen = false;
    }

    openRenameModal(): void {
        this.isRenameModalOpen = true;
    }

    closeDeleteModal(): void {
        this.isDeleteModalOpen = false;
    }

    openDeleteModal(): void {
        this.isDeleteModalOpen = true;
    }

    beforeUpdateColumn(column: KanbanColumn): void {
        this.updatingColumn = column; // store a reference to the column being updated
        this.temporaryColumnName = column.title; // pass the name to the form
        this.openRenameModal();
    }

    beforeDeleteColumn(column: KanbanColumn): void {
        this.updatingColumn = column;
        this.temporaryColumnName = column.title;
        this.isDeleteModalOpen = true;
    }

    onUpdateColumn(name: string): void {
        if (name.trim()) {
            this.updatingColumn.title = name;
            this.columnNameUpdated.emit({ id: this.updatingColumn.data.id, name });
            this.closeRenameModal();
        }

        this.resetModifications();
    }

    onDeleteColumn(): void {
        if (this.updatingColumn && this.updatingColumn.cards?.length > 0) {
            return;
        }

        this.columnDeleted.emit(this.updatingColumn.data.id);
        this.resetModifications();
        this.isDeleteModalOpen = false;
    }

    resetModifications(): void {
        this.updatingColumn = null;
    }

    onDropCard(card: { id: number; index: number; data: { atsJobFunnelId: number; showMessages: boolean } }): void {
        // use time out to let the binding update for the index
        setTimeout(() => {
            this.applicantOrderChanged.emit({
                applicantId: card.id,
                order: card.index,
                atsJobFunnelId: card.data.atsJobFunnelId,
                showMessages: card.data.showMessages,
            });
        }, 0);
    }

    trackAddEmailAutomationOnJobFunnelTemplate(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingFunnelMeatballClickAddEmailAutomation);
    }

    trackEditEmailAutomationOnJobFunnelTemplate(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingFunnelMeatballClickEditEmailAutomation);
    }
}
